---
id: payment-service-go
title: Payment service
owner: Backend Team
status: Active
lastReviewed: 2026-02-02
visibility: internal
tags:
  - payment
  - billing
  - stripe
  - microservice
---

## Product Overview

### Who it serves

This microservice supports Rezi customers and enterprise admins by handling payments, subscriptions, coupons, and billing history for both individual and enterprise plans.

### Primary workflow

Clients request pricing or create payment intents, then complete checkout via Stripe. Enterprise users can retrieve billing summaries, billing history, and manage subscriptions through dedicated endpoints and Stripe portal URLs, while Stripe webhooks update subscription and invoice state.

### Business rules

Legacy `/payment` and `/coupon` routes remain active while `/v1/payments` introduces authenticated endpoints; enterprise routes require enterprise validation. Coupon validation and payment creation depend on Stripe configuration, and transfer flows validate Stripe customer and RMS records before moving ownership.

### Outcomes

Stripe customer records, payment intents, subscriptions, and metered usage updates are stored or synchronized to Firestore and Postgres repositories for downstream billing reporting.

### Out of scope

Frontend checkout UI and user account management are handled by other services.

### API documentation

[Internal API Documentation](https://wiki.rezi.ai/apis/payment-service-go-api-docs)

## Technical Overview

### Architecture flow

Routes in `apps/micro-services/payment-service-go/pkg/api/routes/routes.go` compose payment, coupon, enterprise, and webhook controllers around Stripe-backed services and repository adapters.

### Entry points

- The API server starts in `apps/micro-services/payment-service-go/cmd/payment/main.go`
- The Stripe customer portal utility is `apps/micro-services/payment-service-go/cmd/customerportal/main.go`
- OpenAPI generation is handled by `apps/micro-services/payment-service-go/cmd/spec/main.go`.

### Key modules

Core payment logic lives in `apps/micro-services/payment-service-go/pkg/services` with Stripe helpers in `apps/micro-services/payment-service-go/pkg/services/stripe.go`, while controllers reside in `apps/micro-services/payment-service-go/pkg/api/controllers` and request models in `apps/micro-services/payment-service-go/pkg/api/requests`.

### Data sources and sinks

PostgreSQL-backed enterprise revenue and customer billing history are read through `db-api` using the shared Go DB API client. Supplementary payment history, Stripe customer IDs, and RMS mappings are stored in Firestore repositories under `apps/micro-services/payment-service-go/pkg/repo/nosql/firestore`.

### Integrations and side effects

The service integrates with Stripe APIs and webhooks, Firebase auth middleware, and internal callers for user, resume, and DB API services to enrich billing data and maintain subscription state.

### Constraints and risks

Stripe webhook handling relies on correct signing secrets and API keys, and billing operations require both Postgres and Firestore connectivity; misconfiguration can lead to incomplete subscription sync or missing billing history.

## Changelog

- 2026-02-03: Can have custom token
- 2026-02-02: Initial documentation.
